import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  Shield, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  ArrowUpRight,
  ArrowDownLeft,
  Eye,
  Download
} from 'lucide-react';

const EscrowSystem = () => {
  const [escrowTransactions, setEscrowTransactions] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    verifier: '',
    dateRange: '30d'
  });
  const [loading, setLoading] = useState(true);

  // Mock data
  const mockTransactions = [
    {
      id: "ESC-001",
      projectId: "PRJ-123",
      projectTitle: "Amazon Forest Conservation",
      projectOwner: "EcoForest Initiative",
      verifierId: "VER-001",
      verifierName: "Carbon Integrity Ltd",
      amount: 2500.00,
      platformCommission: 375.00, // 15%
      verifierAmount: 2125.00,
      status: "held_in_escrow",
      createdAt: "2024-01-20T10:30:00Z",
      milestones: [
        { name: "Initial Review", amount: 625.00, status: "completed", completedAt: "2024-01-22T14:20:00Z" },
        { name: "Field Verification", amount: 1250.00, status: "in_progress", dueDate: "2024-02-15T00:00:00Z" },
        { name: "Final Report", amount: 625.00, status: "pending", dueDate: "2024-02-28T00:00:00Z" }
      ],
      timeline: {
        initiated: "2024-01-20T10:30:00Z",
        expectedCompletion: "2024-02-28T00:00:00Z"
      }
    },
    {
      id: "ESC-002",
      projectId: "PRJ-124",
      projectTitle: "Solar Farm Maharashtra",
      projectOwner: "GreenPower Solutions",
      verifierId: "VER-002",
      verifierName: "EcoVerify International",
      amount: 1800.00,
      platformCommission: 270.00, // 15%
      verifierAmount: 1530.00,
      status: "partial_release",
      createdAt: "2024-01-18T09:15:00Z",
      releasedAmount: 765.00,
      milestones: [
        { name: "Document Review", amount: 450.00, status: "completed", completedAt: "2024-01-20T11:30:00Z" },
        { name: "Site Inspection", amount: 900.00, status: "completed", completedAt: "2024-01-25T16:45:00Z" },
        { name: "Verification Report", amount: 450.00, status: "in_progress", dueDate: "2024-02-10T00:00:00Z" }
      ],
      timeline: {
        initiated: "2024-01-18T09:15:00Z",
        expectedCompletion: "2024-02-10T00:00:00Z"
      }
    },
    {
      id: "ESC-003",
      projectId: "PRJ-125",
      projectTitle: "Wind Energy Kenya",
      projectOwner: "African Wind Power",
      verifierId: "VER-003",
      verifierName: "GreenCert Solutions",
      amount: 3200.00,
      platformCommission: 480.00, // 15%
      verifierAmount: 2720.00,
      status: "completed",
      createdAt: "2024-01-15T14:20:00Z",
      completedAt: "2024-01-28T10:15:00Z",
      releasedAmount: 2720.00,
      milestones: [
        { name: "Initial Assessment", amount: 800.00, status: "completed", completedAt: "2024-01-18T12:30:00Z" },
        { name: "Technical Review", amount: 1600.00, status: "completed", completedAt: "2024-01-24T15:20:00Z" },
        { name: "Final Certification", amount: 800.00, status: "completed", completedAt: "2024-01-28T10:15:00Z" }
      ],
      timeline: {
        initiated: "2024-01-15T14:20:00Z",
        expectedCompletion: "2024-02-15T00:00:00Z",
        actualCompletion: "2024-01-28T10:15:00Z"
      }
    },
    {
      id: "ESC-004",
      projectId: "PRJ-126",
      projectTitle: "Mangrove Restoration",
      projectOwner: "Coastal Conservation Trust",
      verifierId: "VER-001",
      verifierName: "Carbon Integrity Ltd",
      amount: 2200.00,
      platformCommission: 330.00, // 15%
      verifierAmount: 1870.00,
      status: "dispute",
      createdAt: "2024-01-12T11:45:00Z",
      disputeReason: "Disagreement on verification methodology",
      milestones: [
        { name: "Methodology Review", amount: 550.00, status: "completed", completedAt: "2024-01-15T09:30:00Z" },
        { name: "Site Verification", amount: 1100.00, status: "disputed", disputedAt: "2024-01-22T13:20:00Z" },
        { name: "Final Report", amount: 550.00, status: "pending", dueDate: "2024-02-20T00:00:00Z" }
      ],
      timeline: {
        initiated: "2024-01-12T11:45:00Z",
        expectedCompletion: "2024-02-20T00:00:00Z"
      }
    }
  ];

  const mockStats = {
    totalEscrowValue: 47250.00,
    totalCommissionGenerated: 7087.50,
    activeTransactions: 12,
    completedTransactions: 28,
    averageCompletionTime: 18.5, // days
    disputeRate: 2.3, // percentage
    monthlyCommissionRevenue: 15750.00,
    pendingReleases: 8945.00
  };

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setEscrowTransactions(mockTransactions);
      setStats(mockStats);
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'held_in_escrow':
        return 'bg-blue-100 text-blue-800';
      case 'partial_release':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'dispute':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'held_in_escrow':
        return <Shield className="h-4 w-4" />;
      case 'partial_release':
        return <ArrowUpRight className="h-4 w-4" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'dispute':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getMilestoneStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'in_progress':
        return 'text-blue-600 bg-blue-100';
      case 'disputed':
        return 'text-red-600 bg-red-100';
      case 'pending':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const handleMilestoneRelease = async (transactionId, milestoneIndex) => {
    try {
      // Simulate API call
      console.log(`Releasing milestone ${milestoneIndex} for transaction ${transactionId}`);
      
      setEscrowTransactions(prev => 
        prev.map(transaction => {
          if (transaction.id === transactionId) {
            const updatedMilestones = [...transaction.milestones];
            updatedMilestones[milestoneIndex] = {
              ...updatedMilestones[milestoneIndex],
              status: 'completed',
              completedAt: new Date().toISOString()
            };

            const completedAmount = updatedMilestones
              .filter(m => m.status === 'completed')
              .reduce((sum, m) => sum + m.amount, 0);

            return {
              ...transaction,
              milestones: updatedMilestones,
              releasedAmount: completedAmount,
              status: completedAmount === transaction.verifierAmount ? 'completed' : 'partial_release'
            };
          }
          return transaction;
        })
      );

      alert('Milestone payment released successfully!');
    } catch (error) {
      console.error('Error releasing milestone payment:', error);
      alert('Error releasing payment. Please try again.');
    }
  };

  const filteredTransactions = escrowTransactions.filter(transaction => {
    const matchesStatus = !filters.status || transaction.status === filters.status;
    const matchesVerifier = !filters.verifier || transaction.verifierName.toLowerCase().includes(filters.verifier.toLowerCase());
    return matchesStatus && matchesVerifier;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Payment Escrow System</h1>
        <p className="text-gray-600">Monitor and manage verification payments and platform commissions</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Escrow Value</p>
              <p className="text-2xl font-bold text-gray-900">${stats.totalEscrowValue?.toLocaleString()}</p>
              <p className="text-xs text-blue-600">Funds held securely</p>
            </div>
            <Shield className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Commission Revenue</p>
              <p className="text-2xl font-bold text-gray-900">${stats.monthlyCommissionRevenue?.toLocaleString()}</p>
              <p className="text-xs text-green-600">This month</p>
            </div>
            <DollarSign className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeTransactions}</p>
              <p className="text-xs text-yellow-600">In progress</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg. Completion</p>
              <p className="text-2xl font-bold text-gray-900">{stats.averageCompletionTime} days</p>
              <p className="text-xs text-purple-600">Processing time</p>
            </div>
            <CheckCircle className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="held_in_escrow">Held in Escrow</option>
            <option value="partial_release">Partial Release</option>
            <option value="completed">Completed</option>
            <option value="dispute">Dispute</option>
          </select>

          <input
            type="text"
            placeholder="Search verifier..."
            value={filters.verifier}
            onChange={(e) => setFilters(prev => ({ ...prev, verifier: e.target.value }))}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />

          <select
            value={filters.dateRange}
            onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      {/* Transactions List */}
      <div className="space-y-6">
        {filteredTransactions.map((transaction) => (
          <div key={transaction.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
            {/* Transaction Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 mr-3">
                      {transaction.projectTitle}
                    </h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(transaction.status)}`}>
                      {getStatusIcon(transaction.status)}
                      <span className="ml-1 capitalize">{transaction.status.replace('_', ' ')}</span>
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Transaction ID:</span>
                      <p>{transaction.id}</p>
                    </div>
                    <div>
                      <span className="font-medium">Project Owner:</span>
                      <p>{transaction.projectOwner}</p>
                    </div>
                    <div>
                      <span className="font-medium">Verifier:</span>
                      <p>{transaction.verifierName}</p>
                    </div>
                    <div>
                      <span className="font-medium">Created:</span>
                      <p>{new Date(transaction.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">${transaction.amount.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">Total Contract</p>
                </div>
              </div>
            </div>

            {/* Financial Breakdown */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                <div className="text-center">
                  <p className="text-gray-600">Contract Amount</p>
                  <p className="font-semibold text-gray-900">${transaction.amount.toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-600">Platform Commission (15%)</p>
                  <p className="font-semibold text-purple-600">${transaction.platformCommission.toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-600">Verifier Amount</p>
                  <p className="font-semibold text-blue-600">${transaction.verifierAmount.toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-600">Released</p>
                  <p className="font-semibold text-green-600">
                    ${(transaction.releasedAmount || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Milestones */}
            <div className="px-6 py-4">
              <h4 className="text-md font-medium text-gray-900 mb-4">Payment Milestones</h4>
              
              <div className="space-y-3">
                {transaction.milestones.map((milestone, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center mb-1">
                        <h5 className="font-medium text-gray-900 mr-3">{milestone.name}</h5>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMilestoneStatusColor(milestone.status)}`}>
                          {milestone.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">${milestone.amount.toLocaleString()}</span>
                        {milestone.completedAt && (
                          <span className="ml-4">Completed: {new Date(milestone.completedAt).toLocaleDateString()}</span>
                        )}
                        {milestone.dueDate && milestone.status !== 'completed' && (
                          <span className="ml-4">Due: {new Date(milestone.dueDate).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {milestone.status === 'in_progress' && (
                        <button
                          onClick={() => handleMilestoneRelease(transaction.id, index)}
                          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg transition duration-200"
                        >
                          Release Payment
                        </button>
                      )}
                      {milestone.status === 'disputed' && (
                        <button className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs rounded-lg transition duration-200">
                          Resolve Dispute
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Progress Bar */}
              <div className="mt-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Payment Progress</span>
                  <span>
                    {Math.round(((transaction.releasedAmount || 0) / transaction.verifierAmount) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((transaction.releasedAmount || 0) / transaction.verifierAmount) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Timeline */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Initiated:</span>
                  <p className="font-medium">{new Date(transaction.timeline.initiated).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-gray-600">Expected Completion:</span>
                  <p className="font-medium">{new Date(transaction.timeline.expectedCompletion).toLocaleDateString()}</p>
                </div>
                {transaction.timeline.actualCompletion && (
                  <div>
                    <span className="text-gray-600">Actual Completion:</span>
                    <p className="font-medium text-green-600">{new Date(transaction.timeline.actualCompletion).toLocaleDateString()}</p>
                  </div>
                )}
              </div>

              {/* Dispute Information */}
              {transaction.status === 'dispute' && transaction.disputeReason && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center mb-2">
                    <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                    <span className="font-medium text-red-900">Dispute Active</span>
                  </div>
                  <p className="text-sm text-red-700">{transaction.disputeReason}</p>
                  <div className="mt-2 flex space-x-2">
                    <button className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg">
                      View Details
                    </button>
                    <button className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg">
                      Mediate
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <div className="flex space-x-2">
                  <button className="text-blue-600 hover:text-blue-800 text-sm flex items-center">
                    <Eye className="h-4 w-4 mr-1" />
                    View Details
                  </button>
                  <button className="text-gray-600 hover:text-gray-800 text-sm flex items-center">
                    <Download className="h-4 w-4 mr-1" />
                    Export
                  </button>
                </div>
                
                <div className="text-xs text-gray-500">
                  Last updated: {new Date().toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary Footer */}
      <div className="mt-8 bg-white rounded-lg shadow-sm border p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-sm text-gray-600">Total Platform Commission</p>
            <p className="text-2xl font-bold text-green-600">${stats.totalCommissionGenerated?.toLocaleString()}</p>
            <p className="text-xs text-gray-500">Lifetime earnings</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Pending Releases</p>
            <p className="text-2xl font-bold text-yellow-600">${stats.pendingReleases?.toLocaleString()}</p>
            <p className="text-xs text-gray-500">Awaiting milestones</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Dispute Rate</p>
            <p className="text-2xl font-bold text-red-600">{stats.disputeRate}%</p>
            <p className="text-xs text-gray-500">Of all transactions</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EscrowSystem; 